import { encodeTrackingContext } from './context';
import { installBrowserEvents } from './browser-events';
import {
  SCHEMA_VERSION,
  SDK_VERSION,
  type Activity,
  type ActivityPayload,
  type ActivityTransport,
  type BrowserContext,
  type EnvironmentContext,
  type FlushOptions,
  type JsonObject,
  type TrackerConfig
} from './contracts';
import { collectBrowserContext, collectEnvironmentContext } from './environment/collect';
import { readExternalIds } from './environment/external-ids';
import {
  installLinkDecoration,
  linkValueFor,
  resolveInboundLink,
  withLinkerParameter
} from './linker';
import type { TrackerLogger } from './logger';
import { getPageContext, pageReferrer } from './page';
import { sanitizeObject, sanitizeString } from './sanitize';
import {
  FIRST_SESSION_UPDATE_MS,
  sessionUpdateDelay,
  SessionClock,
  type SessionCheckpoint
} from './session';
import { IdentityStore, type StoredSession } from './storage';
import { anonymousCustomEventProperties } from './event-properties';
import { ClickInteractions, INTERACTION_PROPERTY } from './interactions';

const MAX_BATCH_EVENTS = 20;
const MAX_PAYLOAD_BYTES = 60 * 1_024;
// A longer Retry-After would hold activity back past the visit it belongs to.
const MAX_RETRY_AFTER_MS = 60_000;
const ANONYMOUS_ACTIVITY_TYPES = new Set([
  'click',
  'consent_granted',
  'custom_event',
  'form_submit',
  'page_view',
  'section_engagement',
  'scroll_depth',
  'session_end',
  'session_update'
]);
const ANONYMOUS_ELEMENT_SELECTOR = /^body(?: > [a-z][a-z0-9-]*(?::nth-of-type\([1-9]\d{0,3}\))?){0,11}$/;

type QueuedActivity = {
  activity: Activity;
  sessionId: string;
  sessionReferrer: StoredSession['referrer'];
  /** True while a send carries this activity; another send must not take it. */
  sending: boolean;
};

type Batch = {
  entries: QueuedActivity[];
  payload: ActivityPayload;
};

const payloadBytes = (payload: ActivityPayload): number => new TextEncoder().encode(JSON.stringify(payload)).byteLength;

const anonymousProperties = (
  type: string,
  properties: Record<string, unknown> | undefined
): JsonObject => {
  const source = properties ?? {};
  const result: JsonObject = {};
  const copyNumber = (key: string, minimum: number, maximum: number): void => {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      result[key] = Math.min(maximum, Math.max(minimum, value));
    }
  };
  const copyString = (key: string, maximumLength: number): void => {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) result[key] = sanitizeString(value, maximumLength);
  };

  if (type === 'page_view' && typeof source.initial === 'boolean') result.initial = source.initial;
  if (type === 'page_view') copyNumber('firstSessionUpdateMs', 1_000, 60_000);
  if (type === 'click') {
    copyNumber('xPercent', 0, 100);
    copyNumber('yPercent', 0, 100);
    copyString('viewport', 20);
    copyString('name', 100);
    copyString('section', 100);
    const elementSelector = source.elementSelector;
    const elementX = source.elementXPercent;
    const elementY = source.elementYPercent;
    const viewportWidth = source.viewportWidthBucket;
    const viewportHeight = source.viewportHeightBucket;
    const hasSafeElementCoordinates = source.coordinateSpace === 'element_relative'
      && typeof elementSelector === 'string'
      && ANONYMOUS_ELEMENT_SELECTOR.test(elementSelector)
      && typeof elementX === 'number'
      && Number.isFinite(elementX)
      && elementX >= 0
      && elementX <= 100
      && typeof elementY === 'number'
      && Number.isFinite(elementY)
      && elementY >= 0
      && elementY <= 100
      && typeof viewportWidth === 'number'
      && Number.isFinite(viewportWidth)
      && viewportWidth >= 160
      && viewportWidth <= 10_000
      && typeof viewportHeight === 'number'
      && Number.isFinite(viewportHeight)
      && viewportHeight >= 120
      && viewportHeight <= 10_000;
    if (hasSafeElementCoordinates) {
      result.coordinateSpace = 'element_relative';
      result.elementSelector = sanitizeString(elementSelector, 500);
      result.elementXPercent = elementX;
      result.elementYPercent = elementY;
      result.viewportWidthBucket = viewportWidth;
      result.viewportHeightBucket = viewportHeight;
    }
  }
  if (type === 'custom_event') {
    copyString('name', 100);
    Object.assign(result, anonymousCustomEventProperties(source));
  }
  if (type === 'form_submit') copyString('name', 100);
  if (type === 'section_engagement') {
    copyString('name', 100);
    copyNumber('activeMs', 0, 24 * 60 * 60 * 1_000);
    copyNumber('maxVisiblePercent', 0, 100);
    copyNumber('viewCount', 0, 1_000);
    copyString('reason', 30);
  }
  if (type === 'scroll_depth') copyNumber('percent', 0, 100);
  if (type === 'session_end' || type === 'session_update') {
    copyNumber('activeMs', 0, 24 * 60 * 60 * 1_000);
    copyNumber('sessionSpanMs', 0, 24 * 60 * 60 * 1_000);
    copyString('reason', 30);
  }
  return result;
};

const describeError = (error: unknown): { name: string; message: string; stack: string | null } => {
  if (error instanceof Error) {
    return {
      name: sanitizeString(error.name || 'Error', 200),
      message: sanitizeString(error.message || 'Unknown error', 2_000),
      stack: error.stack ? sanitizeString(error.stack, 4_000) : null
    };
  }

  return {
    name: 'Error',
    message: sanitizeString(typeof error === 'string' ? error : String(error), 2_000),
    stack: null
  };
};

export class UplaneTracker {
  private readonly clickInteractions = new ClickInteractions();
  private readonly identityStore: IdentityStore;
  private readonly visitorId: string;
  private readonly sessionClock: SessionClock;
  private readonly browserContext: BrowserContext;
  private readonly environmentPromise: Promise<EnvironmentContext>;
  private environment: EnvironmentContext | null = null;
  private retryNotBefore = 0;
  private activities: QueuedActivity[] = [];
  private traits: JsonObject = {};
  private flushPromise: Promise<void> | null = null;
  private flushTimer: number | null = null;
  private sessionUpdateTimer: number | null = null;
  private sessionUpdatesSent = 0;
  private disposeBrowserEvents: (() => void) | null = null;
  private disposeLinkDecoration: (() => void) | null = null;
  private stopped = false;
  private flushAfterStop = false;
  private documentEnded = false;

  constructor(
    private readonly config: TrackerConfig,
    private readonly transport: ActivityTransport,
    private readonly logger: TrackerLogger
  ) {
    this.identityStore = new IdentityStore(config, logger);
    const now = Date.now();
    const linked = resolveInboundLink(config, logger);
    if (linked) this.identityStore.adopt(linked.visitorId, linked.sessionId, now);
    this.visitorId = this.identityStore.getVisitorId();
    this.sessionClock = new SessionClock(
      this.identityStore.getSession(now, pageReferrer(config.privacyMode)),
      config.sessionTimeoutMs,
      sessionStartedAt => this.identityStore.createSession(sessionStartedAt, pageReferrer(config.privacyMode)),
      false,
      now
    );
    this.browserContext = collectBrowserContext(logger, config.privacyMode);
    this.environmentPromise = collectEnvironmentContext(logger, config.privacyMode, this.browserContext);
    void this.environmentPromise.then(environment => { this.environment = environment; });
  }

  get identifiers(): { visitorId: string; sessionId: string } {
    return { visitorId: this.visitorId, sessionId: this.sessionClock.sessionId };
  }

  getContext(): string | null {
    if (this.stopped) return null;
    const now = Date.now();
    this.syncSession(now);
    // A submission after inactivity belongs to the new session, like the next browser event.
    this.enqueueSessionCheckpoints(this.sessionClock.recordActivity(now));
    this.persistSession();
    return encodeTrackingContext(this.config, this.identifiers, now);
  }

  async start(options: { captureInitialPageView?: boolean } = {}): Promise<void> {
    if (this.stopped || this.disposeBrowserEvents) return;

    this.disposeBrowserEvents = installBrowserEvents({
      onActivity: (type, properties) => this.track(type, properties),
      onClickStart: event => this.clickInteractions.begin(event),
      onEngagementChange: engaged => this.handleEngagementChange(engaged),
      onFailure: (error, operation) => this.reportFailure(operation, error),
      onFlush: (preferBeacon) => { void this.flush({ preferBeacon }); },
      onPageView: () => this.trackPageView(),
      onSessionEnd: () => this.captureSessionEnd('pagehide')
    }, this.config.privacyMode);
    this.disposeLinkDecoration = installLinkDecoration({
      linkValue: href => this.linkValue(href),
      onFailure: (error, operation) => this.reportFailure(operation, error)
    });

    this.handleEngagementChange(document.visibilityState !== 'hidden' && document.hasFocus());
    if (options.captureInitialPageView !== false) {
      this.trackPageView({ initial: true });
    }
    this.flushTimer = window.setInterval(() => { void this.flush(); }, this.config.flushIntervalMs);
    this.scheduleSessionUpdate();
    await this.environmentPromise;
    await this.flush();
  }

  track(name: string, properties?: Record<string, unknown>): void {
    if (this.stopped) return;
    const type = sanitizeString(name, 100);
    if (!type) {
      this.reportFailure('track', new Error('Activity name must not be empty'));
      return;
    }
    if (this.config.privacyMode === 'anonymous' && !ANONYMOUS_ACTIVITY_TYPES.has(type)) {
      this.logger.debug('privacy_guard', {
        activityType: type,
        outcome: 'dropped',
        privacyMode: this.config.privacyMode
      });
      return;
    }

    const interaction = type === 'click' || type === 'custom_event' ? this.clickInteractions.current() : null;
    const now = interaction?.occurredAt ?? Date.now();
    this.syncSession(now);
    this.enqueueSessionCheckpoints(this.sessionClock.recordActivity(now));
    this.persistSession();
    const safeProperties = this.config.privacyMode === 'anonymous'
      ? anonymousProperties(type, properties)
      : sanitizeObject(properties);
    if (type === 'custom_event' && this.config.privacyMode === 'anonymous') {
      const omittedPropertyCount = Object.keys(properties ?? {}).length - Object.keys(safeProperties).length;
      if (omittedPropertyCount > 0) {
        this.logger.debug('privacy_guard', {
          activityType: type,
          outcome: 'properties_omitted',
          reason: 'anonymous_mode',
          omittedPropertyCount
        });
      }
    }
    if (interaction) safeProperties[INTERACTION_PROPERTY] = interaction.id;
    else delete safeProperties[INTERACTION_PROPERTY];
    this.enqueueActivity(
      this.sessionClock.sessionId,
      this.sessionClock.sessionReferrer,
      type,
      safeProperties,
      now
    );

    if (this.activities.length >= MAX_BATCH_EVENTS) void this.flush();
  }

  decorateUrl(href: string): string {
    if (this.stopped) return href;
    const value = this.linkValue(href);
    return value ? withLinkerParameter(href, value) : href;
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    if (this.config.privacyMode === 'anonymous') {
      this.logger.debug('privacy_guard', {
        operation: 'identify',
        outcome: 'dropped',
        privacyMode: this.config.privacyMode
      });
      return;
    }
    const normalizedUserId = sanitizeString(userId, 500);
    if (!normalizedUserId) {
      this.reportFailure('identify', new Error('userId must not be empty'));
      return;
    }

    this.traits = { ...this.traits, ...sanitizeObject(traits), userId: normalizedUserId };
    this.track('identify');
    void this.flush();
  }

  setTraits(traits: Record<string, unknown>): void {
    if (this.config.privacyMode === 'anonymous') {
      this.logger.debug('privacy_guard', {
        operation: 'set_traits',
        outcome: 'dropped',
        privacyMode: this.config.privacyMode
      });
      return;
    }
    this.traits = { ...this.traits, ...sanitizeObject(traits) };
  }

  flush(options: FlushOptions = {}): Promise<void> {
    if (options.preferBeacon) return this.flushOnExit();
    if (this.stopped || this.activities.length === 0) return Promise.resolve();
    if (this.flushPromise) return this.flushPromise;

    this.flushPromise = this.performFlush().finally(() => {
      this.flushPromise = null;
    });
    return this.flushPromise;
  }

  stop(flushPending = true): void {
    if (this.stopped) return;
    this.flushAfterStop = flushPending;
    this.disposeBrowserEvents?.();
    this.disposeBrowserEvents = null;
    this.clickInteractions.clear();
    this.disposeLinkDecoration?.();
    this.disposeLinkDecoration = null;
    if (flushPending) {
      this.captureSessionEnd('stop');
      void this.flush({ preferBeacon: true });
    }
    else this.activities.length = 0;
    this.stopped = true;
    if (this.flushTimer !== null) window.clearInterval(this.flushTimer);
    if (this.sessionUpdateTimer !== null) window.clearTimeout(this.sessionUpdateTimer);
    this.flushTimer = null;
    this.sessionUpdateTimer = null;
  }

  private async performFlush(): Promise<void> {
    const environment = await this.environmentPromise;
    if (Date.now() < this.retryNotBefore) return;
    await this.sendBatches({}, this.contextWith(environment));
  }

  /**
   * Sends the queue by beacon at once. A page that is hidden or unloads may
   * never run another task, so this waits neither for the fingerprints nor
   * for a send in progress, which keeps its own activities. Without the
   * fingerprints yet, the batch carries the browser context only.
   */
  private flushOnExit(): Promise<void> {
    const environment = this.environment ?? { browser: this.browserContext };
    return this.sendBatches({ preferBeacon: true }, this.contextWith(environment));
  }

  private contextWith(environment: EnvironmentContext): EnvironmentContext {
    const externalIds = readExternalIds(this.logger, this.config.privacyMode);
    return externalIds ? { ...environment, externalIds } : environment;
  }

  private async sendBatches(options: FlushOptions, context: EnvironmentContext): Promise<void> {
    while (!this.stopped || this.flushAfterStop) {
      const batch = this.nextBatch(context);
      if (!batch) return;

      const deliveryContext = {
        privacyMode: this.config.privacyMode,
        policyBasis: this.config.policyBasis,
        visitorId: this.visitorId,
        sessionId: batch.payload.sessionId,
        activityTypes: batch.entries.map(entry => entry.activity.type)
      };

      batch.entries.forEach(entry => { entry.sending = true; });
      const result = await this.transport.send(batch.payload, options);
      if (result.status === 'failed') {
        batch.entries.forEach(entry => { entry.sending = false; });
        if (result.retryAfterMs !== undefined) {
          this.retryNotBefore = Date.now() + Math.min(result.retryAfterMs, MAX_RETRY_AFTER_MS);
        }
        this.logger.debug('send_activity', {
          ...deliveryContext,
          outcome: 'failed',
          reason: result.reason,
          queuedActivities: this.activities.length
        });
        return;
      }

      this.removeEntries(batch.entries);
      this.logger.debug('send_activity', result.status === 'dropped'
        ? { ...deliveryContext, outcome: 'dropped', reason: result.reason, count: batch.entries.length }
        : { ...deliveryContext, outcome: 'sent', count: batch.entries.length, transport: result.transport });
    }
  }

  /** The next activities of one session that no send carries, in one payload within the size limit. */
  private nextBatch(context: EnvironmentContext): Batch | null {
    for (;;) {
      const waiting = this.activities.filter(entry => !entry.sending);
      const first = waiting[0];
      if (!first) return null;

      const candidate: QueuedActivity[] = [];
      for (const entry of waiting) {
        if (candidate.length >= MAX_BATCH_EVENTS || entry.sessionId !== first.sessionId) break;
        candidate.push(entry);
      }
      let entries = candidate;
      let payload = this.buildPayload(entries, context);
      while (entries.length > 1 && payloadBytes(payload) > MAX_PAYLOAD_BYTES) {
        entries = entries.slice(0, Math.ceil(entries.length / 2));
        payload = this.buildPayload(entries, context);
      }
      if (payloadBytes(payload) <= MAX_PAYLOAD_BYTES) return { entries, payload };

      this.removeEntries([first]);
      this.reportFailure('build_activity_batch', new Error('Activity exceeded the maximum payload size'), {
        activityType: first.activity.type
      });
    }
  }

  private removeEntries(entries: readonly QueuedActivity[]): void {
    const removed = new Set(entries);
    this.activities = this.activities.filter(entry => !removed.has(entry));
  }

  private buildPayload(
    entries: readonly QueuedActivity[],
    context: EnvironmentContext
  ): ActivityPayload {
    const [first] = entries;
    const sessionReferrer = first?.sessionReferrer;
    return {
      schemaVersion: SCHEMA_VERSION,
      sdkVersion: SDK_VERSION,
      workspaceId: this.config.workspaceId,
      privacyMode: this.config.privacyMode,
      policyBasis: this.config.policyBasis,
      visitorId: this.visitorId,
      sessionId: first?.sessionId ?? this.sessionClock.sessionId,
      ...(sessionReferrer === undefined ? {} : { sessionReferrer }),
      sentAt: new Date().toISOString(),
      context,
      traits: this.traits,
      activities: entries.map(entry => entry.activity)
    };
  }

  private linkValue(href: string): string | null {
    return linkValueFor(href, this.config, this.identifiers, Date.now());
  }

  private reportFailure(operation: string, error: unknown, metadata?: Record<string, unknown>): void {
    if (this.stopped) return;
    const described = describeError(error);
    this.logger.error(operation, {
      ...sanitizeObject(metadata),
      outcome: 'failed',
      errorName: described.name,
      error: described.message,
      stack: described.stack,
      sessionId: this.sessionClock.sessionId
    });
  }

  private handleEngagementChange(engaged: boolean): void {
    if (this.stopped) return;
    if (engaged) this.documentEnded = false;
    const now = Date.now();
    this.syncSession(now);
    const checkpoints = this.sessionClock.setEngaged(engaged, now);
    this.enqueueSessionCheckpoints(checkpoints);
    this.persistSession();
  }

  private captureSessionEnd(reason: 'pagehide' | 'stop'): void {
    if (this.documentEnded) return;
    this.documentEnded = true;
    const now = Date.now();
    this.syncSession(now);
    const checkpoints = this.sessionClock.end(reason, now);
    this.enqueueSessionCheckpoints(checkpoints);
    this.persistSession();
  }

  private enqueueSessionCheckpoints(checkpoints: readonly SessionCheckpoint[]): void {
    checkpoints.forEach(checkpoint => {
      this.enqueueActivity(
        checkpoint.sessionId,
        checkpoint.sessionReferrer,
        checkpoint.kind === 'end' ? 'session_end' : 'session_update',
        {
          activeMs: checkpoint.activeMs,
          sessionSpanMs: checkpoint.sessionSpanMs,
          reason: checkpoint.reason
        },
        checkpoint.occurredAt
      );
    });
    // A timeout closes one session and opens the next in the same tab. The new
    // session gets its early checkpoints again, as a new page load would.
    if (checkpoints.some(checkpoint => checkpoint.reason === 'timeout')) {
      this.sessionUpdatesSent = 0;
      this.scheduleSessionUpdate();
    }
  }

  /**
   * Arms the next session_update. The wait grows only when a checkpoint was
   * sent: a tick in a page that is hidden or out of focus sends nothing and
   * keeps the wait, so the first checkpoint still comes early when the visitor
   * returns. A pending timer is replaced, never doubled.
   */
  private scheduleSessionUpdate(): void {
    if (this.sessionUpdateTimer !== null) window.clearTimeout(this.sessionUpdateTimer);
    this.sessionUpdateTimer = null;
    if (this.stopped) return;
    this.sessionUpdateTimer = window.setTimeout(() => {
      this.sessionUpdateTimer = null;
      const now = Date.now();
      this.syncSession(now);
      const checkpoints = this.sessionClock.checkpoint(now);
      if (checkpoints.some(checkpoint => checkpoint.kind === 'update')) this.sessionUpdatesSent += 1;
      if (checkpoints.length > 0) {
        this.enqueueSessionCheckpoints(checkpoints);
        this.persistSession();
        void this.flush();
      }
      this.scheduleSessionUpdate();
    }, sessionUpdateDelay(this.sessionUpdatesSent));
  }

  /**
   * A page view states when this page load sends its first session
   * checkpoint, so a report can bound the time of a visit that has none.
   */
  private trackPageView(properties: Record<string, unknown> = {}): void {
    this.track('page_view', { ...properties, firstSessionUpdateMs: FIRST_SESSION_UPDATE_MS });
  }

  private enqueueActivity(
    sessionId: string,
    sessionReferrer: StoredSession['referrer'],
    type: string,
    properties: JsonObject,
    occurredAt: number
  ): void {
    this.activities.push({
      sessionId,
      sessionReferrer,
      sending: false,
      activity: {
        type,
        timestamp: new Date(occurredAt).toISOString(),
        page: getPageContext(this.config.privacyMode),
        properties
      }
    });
  }

  private syncSession(now: number): void {
    this.sessionClock.syncShared(this.identityStore.readSharedSession(), now);
  }

  private persistSession(): void {
    this.identityStore.saveSession(this.sessionClock.snapshot);
    this.sessionClock.markSaved();
  }
}
